/**
 * A CHARTER, RENDERED TO PDF — the signable artefact of a charter authored in
 * the app (migration 0102, Phase 3).
 *
 * The charter is written in the rich-text editor and stored as a ProseMirror
 * document. To be SIGNED it has to become a file, exactly as a change request is
 * produced to a PDF before it can be sent (#200): a signature is against a fixed
 * document, and the sign-off engine fingerprints that file. This walks the stored
 * document and draws it — headings, paragraphs with their bold/italic/links,
 * bullet and numbered lists, and tables (the risk register, the RACI grid) — onto
 * A4 pages, wrapping and paginating so no row is split and no line runs off.
 *
 * pdf-lib is low-level: it draws glyphs at coordinates, with no layout of its own.
 * So the layout is here — a run-based line builder that keeps a word in the face
 * its marks ask for, and a table drawer that sizes each row to its tallest cell.
 * Every string passes through winAnsi first, because the standard PDF faces throw
 * on a character they cannot encode even while measuring it (see pdf-text.ts).
 *
 * THE SAME DOCUMENT PRODUCES THE SAME BYTES, so a regenerated charter matches the
 * fingerprint it was signed against — nothing here reads the clock or anything
 * outside the document, the title, and the parameter block passed in. The
 * parameters (sponsor, PM, budget, dates) are the project's own metadata read
 * once at produce time; a charter is only ever produced while a draft, so a
 * later edit to About cannot change bytes that have already been signed.
 */
import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import type { JSONContent } from '@tiptap/core';
import type { RichDoc } from './rich-text';
import { winAnsi } from './pdf-text';
// THE SHARED PRODUCED-DOCUMENT CHROME (R18). The charter wears the same letterhead,
// teal section bands, teal-label facts table and footer as requirements / change
// request / authority / after-action — one printed-document family, not a bespoke
// one. The tokens imported here are identical to the ones the charter defined
// locally before, so no colour changes; the difference is that the charter now
// routes through pdf-chrome.ts rather than hand-rolling its own title block, facts
// grid, headings and footer (Vinnie, 13 Oct 2026 — the produced-document pass).
import {
  drawLetterhead,
  drawBand,
  wordedFromSlashed,
  preparedSegments,
  INK,
  QUIET,
  RULE,
  SUNK,
  TINT_LABEL,
  BRAND,
} from './pdf-chrome';
import { embedTitleFont } from './pdf-fonts';

const PAGE = { width: 595.28, height: 841.89 }; // A4 in points
const MARGIN = 52;
const WIDTH = PAGE.width - MARGIN * 2;
const FOOT = 34; // room kept at the foot for the page number
const HEAD = 36; // room a continuation page keeps at the top for its running header

const LINK = rgb(0.05, 0.42, 0.55);

type Fonts = { r: PDFFont; b: PDFFont; i: PDFFont; bi: PDFFont };
type Run = { text: string; bold?: boolean; italic?: boolean; under?: boolean; strike?: boolean; link?: boolean };

const fontFor = (run: Run, F: Fonts): PDFFont =>
  run.bold && run.italic ? F.bi : run.bold ? F.b : run.italic ? F.i : F.r;

/** The inline runs of a block node — text with the marks that face it. */
function runsOf(node: JSONContent): Run[] {
  const out: Run[] = [];
  for (const child of node.content ?? []) {
    if (child.type === 'hardBreak') {
      out.push({ text: '\n' });
      continue;
    }
    if (child.type !== 'text') continue;
    const marks = child.marks ?? [];
    const has = (t: string) => marks.some((m) => m.type === t);
    out.push({
      text: child.text ?? '',
      bold: has('bold'),
      italic: has('italic'),
      under: has('underline') || has('link'),
      strike: has('strike'),
      link: has('link'),
    });
  }
  return out;
}

/*
 * The block nodes that each take their own line inside a table cell — a cell can
 * hold several paragraphs or a bullet list, and each one was entered as its own
 * line, so each one prints as its own line.
 */
const CELL_BLOCKS = new Set([
  'paragraph',
  'heading',
  'listItem',
  'bulletList',
  'orderedList',
  'blockquote',
]);

/**
 * The plain text of a node and everything under it — for table cells.
 *
 * A cell is not always one line: two risk entries typed on separate lines are two
 * paragraphs, and must print as two lines, not run together into one
 * ("Missing pagesMisnumbered pages"). So block-level children are joined with a
 * newline (which wrapPlain then lays out as separate lines) and a hard break is a
 * newline too; the inline pieces inside a single paragraph still join seamlessly.
 */
export function plainText(node: JSONContent): string {
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return '\n';
  const kids = node.content ?? [];
  const sep = kids.some((k) => CELL_BLOCKS.has(k.type ?? '')) ? '\n' : '';
  return kids.map(plainText).join(sep);
}

type Word = { text: string; run: Run; br?: boolean };

/** Runs → words, each carrying its face. A hard break is its own token. */
function toWords(runs: Run[]): Word[] {
  const words: Word[] = [];
  for (const run of runs) {
    const parts = winAnsi(run.text).split('\n');
    parts.forEach((part, i) => {
      if (i > 0) words.push({ text: '', run, br: true });
      for (const w of part.split(/\s+/)) if (w) words.push({ text: w, run });
    });
  }
  return words;
}

export type CharterParam = { label: string; value: string };

export async function charterPdf(
  doc: RichDoc,
  meta: {
    title: string;
    project: string;
    preparedBy?: string;
    preparedOn: string;
    status: string;
    params?: CharterParam[];
  }
): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const F: Fonts = {
    r: await pdf.embedFont(StandardFonts.Helvetica),
    b: await pdf.embedFont(StandardFonts.HelveticaBold),
    i: await pdf.embedFont(StandardFonts.HelveticaOblique),
    bi: await pdf.embedFont(StandardFonts.HelveticaBoldOblique),
  };
  const titleFont = await embedTitleFont(pdf);

  let page: PDFPage = pdf.addPage([PAGE.width, PAGE.height]);
  let y = PAGE.height - MARGIN;
  let section = 0; // teal section bands are auto-numbered, like the other documents

  // A continuation page carries a quiet running header (title + hairline), the same
  // as the other produced documents, so a loose charter page still names itself.
  const carryOver = () => {
    const top = y;
    page.drawText(winAnsi(meta.title), { x: MARGIN, y: top - 10, size: 8.5, font: F.r, color: QUIET });
    page.drawLine({ start: { x: MARGIN, y: top - 19 }, end: { x: MARGIN + WIDTH, y: top - 19 }, thickness: 0.75, color: RULE });
    y = top - HEAD;
  };

  const room = (needed: number) => {
    if (y - needed >= MARGIN + FOOT) return;
    page = pdf.addPage([PAGE.width, PAGE.height]);
    y = PAGE.height - MARGIN;
    carryOver();
  };

  /** Lay runs out into lines within `measure`, drawing from the current y. An
   *  optional marker (a bullet or number) is drawn once, level with the first
   *  line, in the gutter to the left of the text. */
  function drawRuns(runs: Run[], x0: number, measure: number, size: number, marker?: string, colour = INK) {
    const leading = size * 1.5;
    const words = toWords(runs);
    // Greedy wrap into lines of [word...].
    const lines: Word[][] = [];
    let line: Word[] = [];
    let lineW = 0;
    const wordW = (w: Word) => fontFor(w.run, F).widthOfTextAtSize(w.text, size);
    const spaceW = (w: Word) => fontFor(w.run, F).widthOfTextAtSize(' ', size);
    for (const w of words) {
      if (w.br) {
        lines.push(line);
        line = [];
        lineW = 0;
        continue;
      }
      const ww = wordW(w);
      const gap = line.length ? spaceW(w) : 0;
      if (line.length && lineW + gap + ww > measure) {
        lines.push(line);
        line = [];
        lineW = 0;
      }
      line.push(w);
      lineW += (line.length > 1 ? gap : 0) + ww;
    }
    if (line.length) lines.push(line);
    if (lines.length === 0) lines.push([]);

    lines.forEach((ln, at) => {
      room(leading);
      y -= leading;
      if (at === 0 && marker) {
        page.drawText(winAnsi(marker), { x: x0 - 16, y, size, font: F.r, color: INK });
      }
      let x = x0;
      ln.forEach((w, i) => {
        if (i > 0) x += spaceW(w);
        const font = fontFor(w.run, F);
        const width = font.widthOfTextAtSize(w.text, size);
        page.drawText(w.text, { x, y, size, font, color: w.run.link ? LINK : colour });
        if (w.run.under) page.drawLine({ start: { x, y: y - 1.5 }, end: { x: x + width, y: y - 1.5 }, thickness: 0.5, color: w.run.link ? LINK : colour });
        if (w.run.strike) page.drawLine({ start: { x, y: y + size * 0.3 }, end: { x: x + width, y: y + size * 0.3 }, thickness: 0.5, color: colour });
        x += width;
      });
    });
  }

  function drawTable(node: JSONContent, indent: number) {
    const rows = (node.content ?? []).filter((r) => r.type === 'tableRow');
    if (!rows.length) return;
    const size = 9.5;
    const pad = 6;
    const leading = size * 1.35;
    const ncol = Math.max(...rows.map((r) => (r.content ?? []).length));
    const colW = (WIDTH - indent) / ncol;
    for (const r of rows) {
      const cells = r.content ?? [];
      const isHead = cells.every((c) => c.type === 'tableHeader');
      const wrapped = cells.map((c) => wrapPlain(plainText(c), colW - pad * 2, size, isHead ? F.b : F.r));
      const height = Math.max(1, ...wrapped.map((w) => w.length)) * leading + pad * 2 - 2;
      room(height);
      y -= height;
      let x = MARGIN + indent;
      cells.forEach((_, i) => {
        if (isHead) page.drawRectangle({ x, y, width: colW, height, color: SUNK });
        page.drawRectangle({ x, y, width: colW, height, borderColor: RULE, borderWidth: 0.75 });
        wrapped[i].forEach((tline, n) => {
          page.drawText(tline, {
            x: x + pad,
            y: y + height - pad - size + 1 - n * leading,
            size,
            font: isHead ? F.b : F.r,
            color: isHead ? QUIET : INK,
          });
        });
        x += colW;
      });
    }
    y -= 4;
  }

  /**
   * THE FACTS BLOCK — the project's governance metadata (sponsor, PM, vendor,
   * contract, budget, dates) drawn as the SHARED teal-label summary table used by
   * requirements / change request / authority, so the signed charter names on its
   * face who authorises it and what it costs (#2) in the one document family
   * rather than a bespoke grey grid. Two label/value pairs per row; empties are
   * dropped by the caller, so an unfilled project simply shows fewer rows or none.
   */
  function drawParams(params: CharterParam[]) {
    if (!params.length) return;
    const size = 9.5;
    const pad = 6;
    const leading = size * 1.35;
    const widths = [0.22, 0.28, 0.22, 0.28].map((w) => w * WIDTH);
    const pairs: CharterParam[][] = [];
    for (let i = 0; i < params.length; i += 2) pairs.push(params.slice(i, i + 2));
    y -= 2;
    for (const pr of pairs) {
      const cells = [pr[0]?.label ?? '', pr[0]?.value ?? '', pr[1]?.label ?? '', pr[1]?.value ?? ''];
      const isLabel = (i: number) => i === 0 || i === 2;
      const wrapped = cells.map((c, i) => wrapPlain(c, widths[i] - pad * 2, size, isLabel(i) ? F.b : F.r));
      const height = Math.max(1, ...wrapped.map((w) => w.length)) * leading + pad * 2 - 2;
      room(height);
      y -= height;
      let x = MARGIN;
      wrapped.forEach((cell, i) => {
        if (isLabel(i) && cells[i]) page.drawRectangle({ x, y, width: widths[i], height, color: TINT_LABEL });
        page.drawRectangle({ x, y, width: widths[i], height, borderColor: RULE, borderWidth: 0.75 });
        cell.forEach((ln, n) =>
          page.drawText(ln, {
            x: x + pad,
            y: y + height - pad - size + 1 - n * leading,
            size,
            font: isLabel(i) ? F.b : F.r,
            color: isLabel(i) ? BRAND : INK,
          }),
        );
        x += widths[i];
      });
    }
    y -= 4;
  }

  /** Plain (single-face) wrap, for table cells. */
  function wrapPlain(text: string, measure: number, size: number, font: PDFFont): string[] {
    const out: string[] = [];
    for (const para of winAnsi(text).split('\n')) {
      let line = '';
      for (const word of para.split(/\s+/).filter(Boolean)) {
        const tried = line ? `${line} ${word}` : word;
        if (font.widthOfTextAtSize(tried, size) <= measure) line = tried;
        else {
          if (line) out.push(line);
          line = word;
        }
      }
      out.push(line);
    }
    return out.length ? out : [''];
  }

  // A section opens with the shared teal numbered band (drawBand). Sections are
  // auto-numbered (1, 2, 3…) so the charter reads as numbered clauses, like the
  // other documents — and any number the author already typed at the start of the
  // heading is stripped first, so we never print "1 1. Background" (Vinnie, 13 Oct).
  function band(rawTitle: string) {
    const title = rawTitle.replace(/^\s*\d+[.)]?\s+/, '');
    const height = 26;
    room(height + 30);
    y -= height + 8;
    section += 1;
    drawBand(page, { x: MARGIN, yBottom: y, width: WIDTH, title: winAnsi(title), number: section, bold: F.b });
    y -= 12;
  }

  function block(node: JSONContent, indent: number, ordinal?: string) {
    switch (node.type) {
      case 'heading': {
        const level = (node.attrs?.level as number) ?? 2;
        if (level === 2) {
          band(plainText(node));
        } else {
          // Level 3 → the one shared teal sub-heading (11pt teal), as the other docs.
          y -= 12;
          room(16);
          drawRuns(runsOf(node), MARGIN + indent, WIDTH - indent, 11, undefined, BRAND);
          y -= 4;
        }
        break;
      }
      case 'paragraph':
        drawRuns(runsOf(node), MARGIN + indent, WIDTH - indent, 10, ordinal);
        y -= 5;
        break;
      case 'bulletList':
        (node.content ?? []).forEach((li) => listItem(li, indent, '•'));
        y -= 2;
        break;
      case 'orderedList': {
        let n = (node.attrs?.start as number) ?? 1;
        (node.content ?? []).forEach((li) => listItem(li, indent, `${n++}.`));
        y -= 2;
        break;
      }
      case 'blockquote':
        (node.content ?? []).forEach((child) => block(child, indent + 16));
        break;
      case 'horizontalRule':
        y -= 8;
        room(8);
        page.drawLine({ start: { x: MARGIN, y }, end: { x: MARGIN + WIDTH, y }, thickness: 0.75, color: RULE });
        y -= 4;
        break;
      case 'table':
        drawTable(node, indent);
        break;
      default:
        (node.content ?? []).forEach((child) => block(child, indent));
    }
  }

  function listItem(li: JSONContent, indent: number, marker: string) {
    const kids = li.content ?? [];
    kids.forEach((child, i) => {
      if (i === 0 && child.type === 'paragraph') {
        drawRuns(runsOf(child), MARGIN + indent + 18, WIDTH - indent - 18, 10, marker);
        y -= 3;
      } else {
        block(child, indent + 18);
      }
    });
  }

  // ── Letterhead (shared chrome, R18) ──────────────────────────────────────────
  // The eyebrow names the KIND, the big subject names what the charter is ABOUT
  // (the project), a meta line states when it was prepared and its status, and the
  // teal hairline closes it — identical to requirements / change request /
  // authority / after-action. The charter used to hand-draw its own title block;
  // it now routes through the shared drawLetterhead, so the family shares one
  // letterhead and the charter carries a meta line it never had before.
  y = drawLetterhead(
    page,
    { x: MARGIN, width: WIDTH, top: PAGE.height - MARGIN },
    { regular: F.r, bold: F.b, title: titleFont },
    {
      eyebrow: 'Project charter',
      subject: meta.project,
      meta: [...preparedSegments(meta.preparedBy, meta.preparedOn), `Status: ${meta.status}`].join('  ·  '),
    },
  );

  // ── The parameter block (who authorises it, what it costs, when) ─────────────
  drawParams(meta.params ?? []);

  // ── The charter itself ───────────────────────────────────────────────────────
  for (const node of doc.content ?? []) block(node, 0);

  // ── Footer, once the page count is known — hairline + identity line left, page
  //    n of m right, the shared produced-document foot. ────────────────────────
  const foot = `${meta.project}  ·  Project charter  ·  Prepared ${wordedFromSlashed(meta.preparedOn)}`;
  const pages = pdf.getPages();
  pages.forEach((p, i) => {
    p.drawLine({ start: { x: MARGIN, y: MARGIN + 22 }, end: { x: MARGIN + WIDTH, y: MARGIN + 22 }, thickness: 0.75, color: RULE });
    p.drawText(winAnsi(foot), { x: MARGIN, y: MARGIN + 10, size: 7.5, font: F.r, color: QUIET });
    const label = `Page ${i + 1} of ${pages.length}`;
    p.drawText(winAnsi(label), {
      x: MARGIN + WIDTH - F.r.widthOfTextAtSize(label, 7.5),
      y: MARGIN + 10,
      size: 7.5,
      font: F.r,
      color: QUIET,
    });
  });

  return pdf.save();
}
